// Client-side validation for the 'confidenceScore' admin setting
// (AHA-6796). Mirrors the scalar rules of the server-side
// ConfidenceScoreConfigSchema: weights in [0,1] summing to 1, positive
// points, capValue/threshold in [0,100], and band thresholds strictly
// descending in display order with the last band at 0. The field catalogs
// (haveFields/hardGateFields/wantFields) are rendered read-only v1 and are
// not validated here. An empty result object means the form is valid.

export type ConfidenceScoreBandDraft = {
  key: string;
  label: string;
  min: number | undefined;
};

export type ConfidenceScoreSettingsDraft = {
  haveWeight: number | undefined;
  wantWeight: number | undefined;
  requiredPoints: number | undefined;
  conditionalPoints: number | undefined;
  recommendedPoints: number | undefined;
  optionalPoints: number | undefined;
  capValue: number | undefined;
  hardGateMode: 'block' | 'notify';
  hardGateThreshold: number | undefined;
  // Descending-threshold display order; key/label fixed by config.
  bands: ConfidenceScoreBandDraft[];
};

export interface ConfidenceScoreErrors {
  fields?: Record<string, string>;
  bands?: Record<number, string>;
}

const WEIGHT_SUM_TOLERANCE = 1e-6;

function isNumber(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value);
}

export function validateConfidenceScoreSettings(
  draft: ConfidenceScoreSettingsDraft
): ConfidenceScoreErrors {
  const errors: ConfidenceScoreErrors = {};
  const fieldErrors: Record<string, string> = {};
  const bandErrors: Record<number, string> = {};

  (['haveWeight', 'wantWeight'] as const).forEach((key) => {
    const value = draft[key];
    if (!isNumber(value)) {
      fieldErrors[key] = 'Enter a number';
    } else if (value < 0 || value > 1) {
      fieldErrors[key] = 'Must be between 0 and 1';
    }
  });
  if (
    isNumber(draft.haveWeight) &&
    isNumber(draft.wantWeight) &&
    !fieldErrors.haveWeight &&
    !fieldErrors.wantWeight &&
    Math.abs(draft.haveWeight + draft.wantWeight - 1) > WEIGHT_SUM_TOLERANCE
  ) {
    fieldErrors.wantWeight = 'Weights must sum to 1';
  }

  (
    [
      'requiredPoints',
      'conditionalPoints',
      'recommendedPoints',
      'optionalPoints'
    ] as const
  ).forEach((key) => {
    const value = draft[key];
    if (!isNumber(value)) {
      fieldErrors[key] = 'Enter a number';
    } else if (value <= 0) {
      fieldErrors[key] = 'Must be greater than 0';
    }
  });

  (['capValue', 'hardGateThreshold'] as const).forEach((key) => {
    const value = draft[key];
    if (!isNumber(value)) {
      fieldErrors[key] = 'Enter a number';
    } else if (value < 0 || value > 100) {
      fieldErrors[key] = 'Must be between 0 and 100';
    } else if (key === 'hardGateThreshold' && !Number.isInteger(value)) {
      // Spec: the gate threshold is a 0-100 INTEGER (it compares against
      // the rounded overall score).
      fieldErrors[key] = 'Must be a whole number';
    }
  });

  draft.bands.forEach((band, index) => {
    if (!isNumber(band.min)) {
      bandErrors[index] = 'Enter a number';
      return;
    }
    if (band.min < 0 || band.min > 100) {
      bandErrors[index] = 'Must be between 0 and 100';
      return;
    }
    if (index === draft.bands.length - 1 && band.min !== 0) {
      bandErrors[index] = 'The lowest band must start at 0';
      return;
    }
    if (index > 0) {
      const previous = draft.bands[index - 1].min;
      if (isNumber(previous) && band.min >= previous) {
        bandErrors[index] = 'Must be below the band above';
      }
    }
  });

  if (Object.keys(fieldErrors).length > 0) {
    errors.fields = fieldErrors;
  }
  if (Object.keys(bandErrors).length > 0) {
    errors.bands = bandErrors;
  }
  return errors;
}
